import { useActionSheet } from "@expo/react-native-action-sheet";
import {
  formatPrice,
  formatReservationDate,
  isWeb,
  match,
  type PaymentFragment,
  useGetReservationsQuery,
  useGetVirtualWalletBalanceQuery,
  useListPaymentHistoryQuery,
  useListPaymentMethodsQuery,
  useRemovePaymentMethodMutation,
} from "@stepapp/core";
import { PaymentStatus } from "@stepapp/core/apiTypes";
import { Block, Divider, Loader, Modal, Snackbar, spacing, Typography } from "@stepapp/ui";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, type ListRenderItem, StyleSheet } from "react-native";
import {
  Cards,
  CouponModal,
  loaderRef,
  StepappWallet,
  StripeSafePayment,
  VATInvoiceForm,
} from "../../src/components";
import { useAppSelector, useRedeemCoupon } from "../../src/hooks";
import { AppStorage } from "../../src/services/AppStorage";
import { OngoingReservationStatuses } from "../../src/store/types";

const Payments = () => {
  const { t } = useTranslation();
  const { data } = useGetVirtualWalletBalanceQuery();
  const { showActionSheetWithOptions } = useActionSheet();
  const billingAddress = useAppSelector(({ user }) => user?.invoiceData);
  const virtualWallet = data?.getVirtualWalletBalance;
  const priceFormatter = formatPrice(billingAddress?.country || AppStorage().get("countryCode"));

  const [couponModalVisible, setCouponModalVisible] = useState(false);
  const [successSnackbarVisible, setSuccessSnackbarVisible] = useState(false);
  const [errorSnackbarVisible, setErrorSnackbarVisible] = useState(false);
  const [removeCardInfoModalVisible, setRemoveCardInfoModalVisible] = useState(false);

  const { data: transactionsData, loading: transactionsLoading } = useListPaymentHistoryQuery({
    fetchPolicy: "cache-and-network",
  });
  const {
    data: paymentMethods,
    loading: paymentMethodsLoading,
    refetch: refreshPaymentMethods,
  } = useListPaymentMethodsQuery({
    fetchPolicy: "cache-and-network",
  });

  const { data: reservations } = useGetReservationsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      filter: { status: OngoingReservationStatuses },
      isCustomerApp: true,
    },
  });

  const [removePaymentMethod] = useRemovePaymentMethodMutation();

  const { redeemCoupon, loadingCoupon, title, description } = useRedeemCoupon({
    onSuccess: () => {
      setCouponModalVisible(false);
      setSuccessSnackbarVisible(true);
    },
    onError: () => {
      setErrorSnackbarVisible(true);
    },
  });

  const hasPaymentMethods =
    paymentMethods?.listPaymentMethods && paymentMethods?.listPaymentMethods?.length > 0;
  const transactions = useMemo(() => {
    if (!transactionsData?.listPaymentsHistory) return [];

    return Object.entries(transactionsData?.listPaymentsHistory).map(
      ([dtCreated, transaction]) => ({ dtCreated, ...transaction }),
    );
  }, [transactionsData?.listPaymentsHistory]);

  const handleDetachCard = useCallback(
    async (cardId: string) => {
      loaderRef.current?.showLoader();
      const { errors } = await removePaymentMethod({
        variables: { paymentMethodId: cardId },
      });
      await refreshPaymentMethods();
      loaderRef.current?.hideLoader();

      if (errors) {
        throw new Error(errors[0]?.message);
      }
    },
    [removePaymentMethod, refreshPaymentMethods],
  );

  const removeCard = useCallback(
    (cardId: string) => {
      showActionSheetWithOptions(
        {
          title: t("screens.profile.removeCardExplanation"),
          options: [t("screens.profile.removeCard"), t("common.return")],
          destructiveButtonIndex: 0,
          cancelButtonIndex: 1,
          useModal: true,
        },
        (index) => {
          if (index === 0) {
            if (
              paymentMethods?.listPaymentMethods.length === 1 &&
              reservations?.listReservations &&
              reservations?.listReservations?.length > 0
            ) {
              return setRemoveCardInfoModalVisible(true);
            }
            handleDetachCard(cardId);
          }
        },
      );
    },
    [
      handleDetachCard,
      showActionSheetWithOptions,
      paymentMethods?.listPaymentMethods,
      reservations?.listReservations,
      t,
    ],
  );

  // TODO add VAT invoice
  const renderItem: ListRenderItem<PaymentFragment> = useCallback(
    ({ item }) => {
      const walletDiscount =
        -item?.appliedDiscount > (item?.amount || 0) ? -item?.amount : item?.appliedDiscount;
      const priceAfterDiscount = item?.amount + walletDiscount;

      return (
        <Block paddingVertical={2}>
          <Block alignCenter justifySpace row paddingBottom={1}>
            <Typography type="secondary">
              {match(item?.status)
                .with(PaymentStatus.Paid, () => t("screens.payments.paid"))
                .with(PaymentStatus.Failed, () => t("screens.payments.failed"))
                .otherwise(() => item?.status)}
            </Typography>
            <Typography type="secondary">
              {formatReservationDate(item?.paymentUpdateDate || "", "dd.MM.yyyy")}
            </Typography>
          </Block>

          {-item?.appliedDiscount > 0 && (
            <Block alignCenter justifySpace row>
              <Typography variant="button" type="error">
                {priceFormatter(walletDiscount, { currency: item?.currency })}
              </Typography>
            </Block>
          )}

          <Block alignCenter justifySpace row>
            {/* // TODO backend */}
            {/* <Typography type="accent" variant="bodyBold">
            {item?.description}
          </Typography> */}
            <Typography variant="button">
              {priceFormatter(priceAfterDiscount, { currency: item?.currency })}
            </Typography>
          </Block>
        </Block>
      );
    },
    [priceFormatter, t],
  );

  return (
    <Block flex={1} paddingTop={isWeb ? 4 : 2}>
      <Typography variant="newH2">{t("screens.payments.title")}</Typography>
      <FlatList
        ItemSeparatorComponent={Divider}
        ListEmptyComponent={() =>
          transactionsLoading || paymentMethodsLoading ? (
            <Loader />
          ) : (
            <Typography type="secondary" variant="paragraph">
              {t("screens.payments.noTransactions")}
            </Typography>
          )
        }
        ListHeaderComponent={() => (
          <Block>
            {hasPaymentMethods && (
              <Block paddingBottom={2} paddingTop={4}>
                <Typography variant="newH3">{t("screens.payments.savedCards")}</Typography>

                <Cards cards={paymentMethods?.listPaymentMethods} onCardPress={removeCard} />

                <StripeSafePayment />
              </Block>
            )}

            <Block paddingBottom={2} paddingTop={hasPaymentMethods ? 0 : 3}>
              <Typography type="secondary" variant="lightLabel">
                {t("screens.payments.editPaymentInfo")}
              </Typography>
            </Block>

            {!!billingAddress?.taxIdentification && (
              <>
                <Divider />
                <Block marginVertical={2}>
                  <Typography variant="newH3">{t("pages.serviceSummary.VATInvoice")}</Typography>
                </Block>
                <VATInvoiceForm
                  city={billingAddress?.city || ""}
                  email={billingAddress?.email || ""}
                  line1={billingAddress?.line1 || ""}
                  line2={billingAddress?.line2 || ""}
                  name={billingAddress?.name || ""}
                  postal_code={billingAddress?.postal_code || ""}
                  taxIdentification={billingAddress?.taxIdentification || ""}
                />
              </>
            )}

            <Divider />

            <Block marginTop={1} paddingBottom={3}>
              <StepappWallet
                isHeadlineVisible
                balance={virtualWallet?.balance || 0}
                onAddPress={() => setCouponModalVisible(true)}
              />

              <Block paddingVertical={4}>
                <Divider />
              </Block>

              <Typography variant="sectionTitle">
                {t("screens.payments.transactionHistory")}
              </Typography>
            </Block>
          </Block>
        )}
        contentContainerStyle={styles.content}
        data={transactions}
        keyExtractor={({ dtCreated }) => dtCreated}
        renderItem={renderItem}
      />

      <Snackbar
        containerStyles={styles.customSnackbar}
        isVisible={!isWeb && successSnackbarVisible}
        setIsVisible={setSuccessSnackbarVisible}
        text={title}
      />

      <CouponModal
        description={description}
        errorSnackbarVisible={errorSnackbarVisible}
        isVisible={couponModalVisible}
        loadingCoupon={loadingCoupon}
        redeemCoupon={redeemCoupon}
        screen="payment"
        setErrorSnackbarVisible={setErrorSnackbarVisible}
        setIsVisible={setCouponModalVisible}
        setSuccessSnackbarVisible={setSuccessSnackbarVisible}
        successSnackbarVisible={successSnackbarVisible}
        title={title}
      />

      <Modal
        description={t("screens.payments.cantRemoveCardDescription")}
        primaryAction={() => setRemoveCardInfoModalVisible(false)}
        primaryActionLabel={t("common.OK")}
        title={t("screens.payments.cantRemoveCardTitle")}
        toggle={() => setRemoveCardInfoModalVisible(false)}
        visible={removeCardInfoModalVisible}
      />
    </Block>
  );
};

export default Payments;

const styles = StyleSheet.create({
  content: {
    paddingBottom: spacing.unit * 4,
  },
  customSnackbar: {
    bottom: spacing.unit * 6,
    marginHorizontal: -spacing.unit * 2.5,
  },
});
